import * as Sentry from '@sentry/react';
import { useCallback, useEffect, useState } from 'react';
import { createBrowserRouter, type LoaderFunctionArgs, RouterProvider as ReactRouterRouterProvider, redirect, redirectDocument, useRouteError } from 'react-router-dom';
import { AppErrorDisplay } from 'src/common/app-error-display';
import { Layout } from 'src/common/components/layout';
import { SwagStoreURL } from 'src/common/constants';
import { gb, gbPromise } from 'src/common/growthbook';
import { useOnUserLogin } from 'src/common/hooks';
import { useFollowUserByUsernameMutation } from 'src/features/dashboard/components/user-connections/user-connections.slice';
import { INVITE_LOCAL_STORAGE_KEY } from 'src/features/invite/invite-page';
import { backToTheLegacyLoader, routeProtector } from './route-protector';
import { routes } from './routes';
export const RouterErrorElement = () => {
  const error = useRouteError();
  useEffect(() => {
    Sentry.captureException(error);
  }, [error]);
  return <AppErrorDisplay error={error} data-sentry-element="AppErrorDisplay" data-sentry-component="RouterErrorElement" data-sentry-source-file="index.tsx" />;
};
export const RouterProvider = () => {
  const [router, setRouter] = useState<ReturnType<typeof createBrowserRouter> | null>(null);
  const [followUser] = useFollowUserByUsernameMutation();
  const handleUserLogin = useCallback(() => {
    const invitedByUsername = localStorage.getItem(INVITE_LOCAL_STORAGE_KEY)?.trim();
    if (!invitedByUsername) return;
    followUser(invitedByUsername).unwrap().then(() => {
      localStorage.removeItem(INVITE_LOCAL_STORAGE_KEY);
    }).catch(

      /* fail silently */);
  }, [followUser]);
  useOnUserLogin(handleUserLogin);
  useEffect(() => {
    async function setupRouter() {
      await gbPromise;
      const sentryCreateBrowserRouter = Sentry.wrapCreateBrowserRouterV6(createBrowserRouter);
      const routerInstance = sentryCreateBrowserRouter([{
        element: <Layout />,
        errorElement: <RouterErrorElement />,
        loader: routeProtector().waitForUserData(),
        children: [...routes, {
          path: '/swag-shop',
          loader: () => redirect(SwagStoreURL),
          element: null
        }, {
          path: '/work-at-thm',
          loader: () => redirect('https://careers.tryhackme.com'),
          element: null
        }, {
          path: '*',
          loader: async ({
            request
          }: LoaderFunctionArgs) => {
            const url = new URL(request.url);
            if (url.pathname.startsWith('/logout') && typeof window !== 'undefined' && window.google?.accounts?.id) {
              // Disable auto login for Google One-Tap (for 24hrs)
              window.google.accounts.id.disableAutoSelect();
            }

            // Check if this is a campaign route (lazy-loaded only when accessed)
            const {
              checkAndHandleCampaignRoute
            } = await import('./sub-routes/influencer-campaigns');
            const campaignResult = await checkAndHandleCampaignRoute(url.pathname);
            if (campaignResult) return campaignResult;
            await gbPromise;
            if (gb.isOn('remove-slash-r-prefix')) {
              if (url.searchParams.get('clientRouterFallback') === 'true') {
                return redirectDocument('/not-found');
              }
              url.searchParams.set('clientRouterFallback', 'true');
              return redirectDocument(url.toString());
            }
            return backToTheLegacyLoader({
              request,
              params: {}
            });
          },
          element: null
        }]
      }, {
        path: '/fullscreen-vms/:tabIndex?',
        loader: routeProtector().requireCompletedOnboardingAndWelcome().requireRoomPaywallCheck(),
        async lazy() {
          const {
            FullscreenVmViewer
          } = await import('src/features/fullscreen-vm-viewer');
          return {
            Component: FullscreenVmViewer
          };
        },
        handle: {
          hideHeader: true,
          hideIntercom: true,
          hideFooter: true
        }
      }], {
        basename: process.env.PUBLIC_URL
      });
      setRouter(routerInstance);
    }
    setupRouter();
  }, []);
  if (!router) return null;
  return <ReactRouterRouterProvider router={router} data-sentry-element="ReactRouterRouterProvider" data-sentry-component="RouterProvider" data-sentry-source-file="index.tsx" />;
};